import { parseCsv } from "./rosterCsv";

/**
 * One file holding customers and their plans (owner 2026-11-01): the browser takes
 * out the plan columns for radagent. Only these columns leave: a password, a name
 * or a mobile can never reach a model, whatever else the file holds.
 */
export const PLAN_COLUMNS = ["plan_name", "price", "validity_days", "speed_mbps", "product", "devices", "daily_minutes", "prices_include_gst"];
const PLAN_REQUIRED = ["plan_name", "price", "validity_days", "speed_mbps"];

/** A row as radagent compares it. */
export interface PlanRow {
  source_name: string;
  speed_mbps: number;
  days: number;
  devices: number;
  price: number;
  includes_gst?: boolean;
  product?: string;
}

export interface PlanSplit {
  /** No plan details: an ordinary customer file, nothing to split. */
  consolidated: boolean;
  plansCsv: string;
  planCount: number;
  rows: PlanRow[];
  /** A plan name with two different rows: both are kept, the operator decides. */
  conflicts: string[];
  problem?: string;
}

const cell = (v: string) => (/[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

export function plansFileName(original: string): string {
  return `${original.replace(/\.csv$/i, "")}-plans.csv`;
}

/** What old systems call the plan columns. The operator can change any of them. */
const PLAN_OTHER_NAMES: Record<string, string[]> = {
  price: ["rate", "amount", "plan_price", "mrp"],
  validity_days: ["validity", "days", "duration_days", "plan_days"],
  speed_mbps: ["speed", "mbps", "bandwidth"],
  devices: ["logins", "simultaneous_use"],
  prices_include_gst: ["includes_gst", "gst_included"],
};
export const PLAN_DETAIL_COLUMNS = PLAN_COLUMNS.filter((c) => c !== "plan_name");
export type PlanColumnMap = Record<string, string>;

export function guessPlanColumns(headings: string[]): PlanColumnMap {
  const out: PlanColumnMap = {};
  for (const c of PLAN_DETAIL_COLUMNS) {
    const found = [c, ...(PLAN_OTHER_NAMES[c] ?? [])].map((n) => headings.find((h) => h.toLowerCase() === n)).find(Boolean);
    if (found) out[c] = found;
  }
  return out;
}

/** planName and map: the operator's own column names, else the file must use ours. */
export function splitPlans(text: string, planName?: string, map?: PlanColumnMap): PlanSplit {
  const none: PlanSplit = { consolidated: false, plansCsv: "", planCount: 0, rows: [], conflicts: [] };
  const records = parseCsv(text);
  const rename = new Map<string, string>(Object.entries(map ?? {}).map(([ours, theirs]) => [theirs.toLowerCase(), ours]));
  if (planName) rename.set(planName.toLowerCase(), "plan_name");
  const header = (records[0]?.fields ?? []).map((h) => h.trim().toLowerCase()).map((h) => rename.get(h) ?? h);
  if (!header.includes("plan_name")) return none;
  const missing = PLAN_REQUIRED.filter((c) => !header.includes(c));
  // Only a plan's name: a customer file saying which plan each customer is on.
  if (missing.length === PLAN_REQUIRED.length - 1) return none;
  if (missing.length) {
    return { ...none, consolidated: true, problem: `The file has no ${missing.join(", ")} column, so plans cannot be read from it. Upload a plans file on Propose from a document.` };
  }
  const columns = PLAN_COLUMNS.filter((c) => header.includes(c));
  const at = columns.map((c) => header.indexOf(c));
  const seen = new Set<string>();
  const names = new Map<string, number>();
  const lines: string[] = [];
  const rows: PlanRow[] = [];
  const value = (values: string[], c: string) => values[columns.indexOf(c)] ?? "";
  for (const r of records.slice(1)) {
    const values = at.map((i) => (r.fields[i] ?? "").trim());
    if (!values[0]) continue;
    const line = values.map(cell).join(",");
    if (seen.has(line)) continue;
    seen.add(line);
    lines.push(line);
    const gst = value(values, "prices_include_gst").toLowerCase();
    rows.push({
      source_name: values[0],
      speed_mbps: Number(value(values, "speed_mbps")),
      days: Number(value(values, "validity_days")),
      devices: Number(value(values, "devices")) || 1,
      price: Number(value(values, "price")),
      ...(gst === "yes" ? { includes_gst: true } : gst === "no" ? { includes_gst: false } : {}),
      ...(value(values, "product") ? { product: value(values, "product").toLowerCase() } : {}),
    });
    names.set(values[0], (names.get(values[0]) ?? 0) + 1);
  }
  return {
    consolidated: true,
    plansCsv: [columns.join(","), ...lines].join("\n") + "\n",
    planCount: lines.length,
    rows,
    conflicts: [...names].filter(([, n]) => n > 1).map(([name]) => name),
  };
}
